import { Link, useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeft, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DRILLDOWN_PARAM_KEYS } from './drilldowns';

/**
 * Banner shown at the top of a drilldown destination page (e.g.
 * `/tasks`) when the user arrived via a dashboard tile click.
 *
 *  - **Context.** Renders "Filtered by <dashLabel>" so the user knows
 *    exactly what narrowed the list (e.g. "Filtered by Cathy" or
 *    "Filtered by Referral · open"). Falls back to "Filtered from
 *    dashboard" when no label was passed.
 *  - **Wayfinding.** "← Back to dashboard" rebuilds the exact
 *    dashboard URL (date window + preset) from the carry-through
 *    params.
 *  - **Clear filter.** "× Clear filter" strips ALL drilldown params
 *    (assignedTo / assigneeType / labels / status / from /
 *    dash{From,To,Preset,Label}) so the user can stay on /tasks but
 *    see their unfiltered saved view.
 *  - **Pref isolation.** The destination page reads `from=dashboard`
 *    to suppress writing current filters to the saved tasks.inbox
 *    pref. The banner reminds the user this slice is transient.
 *
 * Renders nothing if `from` !== `dashboard` — drop it into a layout
 * unconditionally and it stays invisible on direct navigations.
 */
/**
 * Pure helper — strips every drilldown-injected key from a URL search
 * object. Exported separately so unit tests can exercise the rule
 * without rendering a router context.
 *
 * Empty / undefined values are dropped along the way so the result is
 * a clean record suitable for `navigate({ search: ... })`.
 */
export function cleanDrilldownParams(
  search: Record<string, string | undefined>
): Record<string, string> {
  const clean: Record<string, string> = {};
  for (const [k, v] of Object.entries(search)) {
    if (v === undefined || v === '') continue;
    if (DRILLDOWN_PARAM_KEYS.has(k)) continue;
    clean[k] = v;
  }
  return clean;
}

export function DrilldownBanner(): React.ReactElement | null {
  const search = useSearch({ strict: false }) as Record<string, string | undefined>;
  const navigate = useNavigate();
  // We render on /tasks, /documents, AND /setup/users today.
  // "Clear filter" must stay on whichever page the user is currently on
  // — hardcoding `/tasks` would teleport users from referrals or users
  // back to tasks unexpectedly.
  const location = useLocation();
  if (search.from !== 'dashboard') return null;

  // Pass through the dashboard's saved date range so the back-link
  // restores the exact view. Absent params fall through to the
  // dashboard's own defaults.
  const dashSearch: Record<string, string> = {};
  if (search.dashFrom) dashSearch.dateFrom = search.dashFrom;
  if (search.dashTo) dashSearch.dateTo = search.dashTo;
  if (search.dashPreset) dashSearch.preset = search.dashPreset;

  const label = search.dashLabel;

  /**
   * Strip every drilldown-injected param from the URL. The user stays
   * on the SAME page (whichever destination they landed on) — the
   * filters reset to whatever their saved view says, or to the bare-URL
   * defaults if they have no saved view.
   */
  const handleClearFilter = (): void => {
    // Use the current pathname so the banner works correctly on every
    // destination page, not just /tasks. `to` accepts a string path
    // and the router treats `replace: true` as a same-page param swap.
    navigate({ to: location.pathname, search: cleanDrilldownParams(search), replace: true });
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 border-b bg-muted/40 px-6 py-2 text-sm'
      )}
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        {label ? (
          <>
            <span>Filtered by</span>
            <span className="font-medium text-foreground">{label}</span>
          </>
        ) : (
          <span className="font-medium text-foreground">Filtered from dashboard</span>
        )}
        <span>· Your saved view is unchanged.</span>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" className="h-7 gap-1 px-2" onClick={handleClearFilter}>
          <X className="h-3.5 w-3.5" />
          Clear filter
        </Button>
        <Link
          to="/dashboard"
          search={dashSearch}
          className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}
